import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Save, Mail, Phone, Award, MapPin } from 'lucide-react';
import ImageUploader from './ImageUploader';
import PhoneInput from '../ui/PhoneInput';
import EmailInput from '../ui/EmailInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExpertProfileEdit({ expert, onUpdate, getAuthHeader }) {
  const [formData, setFormData] = useState({
    photo_profil_url: expert?.photo_profil_url || '',
    email: expert?.email || '',
    telephone: expert?.telephone || '',
    nom: expert?.nom || '',
    prenom: expert?.prenom || '',
    specialite: expert?.specialite || '',
    numero_agrement: expert?.numero_agrement || '',
    territoire: expert?.territoire || ''
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUploaded = (url) => {
    setFormData(prev => ({ ...prev, photo_profil_url: url }));
    toast.success('Photo mise à jour !');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '' && v !== null)
      );

      const response = await axios.put(
        `${BACKEND_URL}/api/users/me/profile`,
        dataToSend,
        { headers: getAuthHeader() }
      );

      toast.success('Profil mis à jour avec succès !');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Photo de profil */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Photo de profil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ImageUploader
            currentImageUrl={formData.photo_profil_url}
            onImageUploaded={handleImageUploaded}
            type="profile"
            getAuthHeader={getAuthHeader}
          />
        </CardContent>
      </Card>

      {/* Informations personnelles */}
      <Card>
        <CardHeader>
          <CardTitle>Informations personnelles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom *
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prénom *
              </label>
              <input
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email *
              </label>
              <EmailInput
                value={formData.email}
                onChange={(val) => setFormData(prev => ({ ...prev, email: val }))}
                placeholder="email@exemple.fr"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Téléphone
              </label>
              <PhoneInput
                value={formData.telephone}
                onChange={(val) => setFormData(prev => ({ ...prev, telephone: val }))}
                placeholder="Numéro de téléphone"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informations professionnelles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Informations professionnelles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Spécialité
              </label>
              <input
                type="text"
                name="specialite"
                value={formData.specialite}
                onChange={handleChange}
                placeholder="Ex: Dégâts des eaux, Incendie..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                N° Habilitation
              </label>
              <input
                type="text"
                name="numero_agrement"
                value={formData.numero_agrement}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {expert?.ref_expert && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Référence Expert
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={expert.ref_expert}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                  <span className="px-3 py-2 bg-purple-700 text-white rounded-lg text-sm font-medium">
                    Automatique
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Généré automatiquement lors de la signature du convention
                </p>
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Territoire d'intervention
              </label>
              <input
                type="text"
                name="territoire"
                value={formData.territoire}
                onChange={handleChange}
                placeholder="Ex: Guadeloupe, Martinique..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bouton de sauvegarde */}
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 px-4 md:px-8"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Enregistrer les modifications
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
